import React, { useState } from 'react';
import ConversationAnalyzer from './Translate';

// Comprehensive topic configurations for all 8 conversation types
const topicConfigurations = {
  // 1. FAVORITE - Talking about favorite things (color, animal, food, game, outside activities)
  favorite: {
    keywords: {
      "favorite": 2.5,
      "like": 1.8,
      "love": 1.8,
      "prefer": 1.6,
      "enjoy": 1.5,
      "best": 1.4,
      "choose": 1.3,
      "pick": 1.3,
      // Colors
      "color": 2.0,
      "red": 1.2, "blue": 1.2, "green": 1.2, "yellow": 1.2, "purple": 1.2, "orange": 1.2, "pink": 1.2, "black": 1.2, "white": 1.2,
      // Animals
      "animal": 2.0,
      "dog": 1.5, "cat": 1.5, "bird": 1.3, "fish": 1.3, "horse": 1.3, "lion": 1.3, "tiger": 1.3, "elephant": 1.3, "bear": 1.3,
      // Food
      "food": 2.0,
      "pizza": 1.4, "burger": 1.4, "ice cream": 1.5, "chocolate": 1.4, "fruit": 1.3, "vegetable": 1.3, "sandwich": 1.3,
      // Games
      "game": 2.0,
      "play": 1.6, "fun": 1.5, "sport": 1.4, "soccer": 1.3, "basketball": 1.3, "video game": 1.5, "board game": 1.4,
      // Outside activities
      "outside": 1.8,
      "outdoor": 1.6, "park": 1.4, "playground": 1.5, "bike": 1.4, "walk": 1.3, "run": 1.3, "swim": 1.4, "hiking": 1.4
    },
    positiveSamples: [
      "My favorite color is blue because it reminds me of the ocean.",
      "I love dogs because they are friendly and loyal pets.",
      "Pizza is my favorite food, especially with pepperoni on top.",
      "I enjoy playing soccer with my friends at the park.",
      "My favorite thing to do outside is riding my bike around the neighborhood.",
      "I like to play video games, especially adventure games.",
      "Red is the best color because it's bright and cheerful."
    ],
    negativeSamples: [
      "I feel sad when it rains because I can't go outside.",
      "We need to solve this problem by working together.",
      "I'm sorry that happened, let's talk about it.",
      "Can you help me find my teacher please?",
      "The grocery store worker was very helpful today."
    ]
  },

  // 2. FEELING - Expressing emotions and what causes them
  feeling: {
    keywords: {
      "feel": 2.5,
      "feeling": 2.5,
      "emotion": 2.0,
      "when": 1.8,
      "because": 1.6,
      "makes me": 1.8,
      // Emotions
      "happy": 2.0, "sad": 2.0, "mad": 2.0, "angry": 2.0, "calm": 2.0, "peaceful": 1.8,
      "confident": 2.0, "nervous": 2.0, "worried": 1.8, "excited": 1.8, "scared": 1.8, "proud": 1.8,
      "frustrated": 1.8, "disappointed": 1.8, "grateful": 1.6, "surprised": 1.6, "confused": 1.6,
      // Emotional triggers
      "smile": 1.4, "cry": 1.4, "laugh": 1.4, "upset": 1.6, "joy": 1.5, "fear": 1.5, "worry": 1.5,
      "glad": 1.4, "sorry": 1.4, "hurt": 1.4, "comfortable": 1.3, "uncomfortable": 1.5
    },
    positiveSamples: [
      "I feel happy when I play with my friends at recess.",
      "I feel sad when someone is mean to me or my friends.",
      "I feel calm when I listen to music or read a good book.",
      "I feel mad when people don't listen to what I'm saying.",
      "I feel confident when I know the answer in class.",
      "I feel nervous when I have to speak in front of many people.",
      "I'm glad when my family spends time together on weekends."
    ],
    negativeSamples: [
      "My favorite animal is a dolphin because they're smart.",
      "We should work together to solve this conflict.",
      "Can you please help me find the bathroom?",
      "I like to eat pizza and ice cream for dinner.",
      "The bakery worker makes delicious cupcakes every day."
    ]
  },

  // 3. LISTENING - Active listening and responding to others' feelings
  listening: {
    keywords: {
      "listen": 2.5,
      "listening": 2.5,
      "hear": 1.8,
      "understand": 2.0,
      "respond": 2.0,
      "reply": 1.8,
      "answer": 1.6,
      "sorry": 2.0,
      "glad": 2.0,
      "that makes": 1.8,
      "I am": 1.5,
      "thank you": 1.8,
      "thanks": 1.6,
      // Empathetic responses
      "I'm sorry": 2.2,
      "I'm glad": 2.2,
      "I understand": 2.0,
      "that's great": 1.8,
      "that's sad": 1.8,
      "that's wonderful": 1.8,
      "I hear you": 1.9,
      "makes sense": 1.6,
      "I see": 1.4,
      "wow": 1.3,
      "really": 1.2,
      "amazing": 1.4,
      "awesome": 1.4
    },
    positiveSamples: [
      "I'm glad that makes you feel happy about your new pet.",
      "I'm sorry that makes you feel sad when friends move away.",
      "I understand that makes you feel nervous about the test.",
      "That's wonderful that you feel confident about your presentation.",
      "I hear you when you say that makes you feel frustrated.",
      "Thank you for sharing how you feel about that situation.",
      "I'm sorry you feel mad when people don't listen to you."
    ],
    negativeSamples: [
      "My favorite color is purple because it's pretty.",
      "Let's solve this problem by taking turns with the toy.",
      "Can you help me buy a ticket for the train please?",
      "I like to eat sandwiches and drink juice for lunch.",
      "The arcade worker showed me how to play the new game."
    ]
  },

  // 4. FRIENDS - Making friends and getting to know people
  friends: {
    keywords: {
      "friend": 2.5,
      "friends": 2.5,
      "name": 2.0,
      "hello": 2.0,
      "hi": 2.0,
      "meet": 1.8,
      "nice": 1.6,
      "kind": 1.8,
      "ask": 1.8,
      "question": 1.8,
      "get to know": 2.0,
      "tell me": 1.8,
      "about you": 1.8,
      "greet": 1.8,
      "greeting": 1.6,
      "introduce": 1.8,
      "conversation": 1.6,
      "talk": 1.5,
      "chat": 1.4,
      "cool": 1.4,
      "awesome": 1.4,
      "interesting": 1.5,
      "goodbye": 1.6,
      "bye": 1.6,
      "see you": 1.5,
      "pleasure": 1.5,
      "subject": 1.4,
      "school": 1.3,
      "outside": 1.3,
      "animal": 1.3,
      "food": 1.3,
      "fun": 1.5
    },
    positiveSamples: [
      "Hello! My name is Sarah. What's your name?",
      "Hi there! Nice to meet you. What's your favorite subject in school?",
      "I'd like to get to know you better. What do you like to do for fun?",
      "That's cool! What's your favorite animal and why?",
      "It's nice talking with you. What do you like to do outside?",
      "Thanks for telling me about yourself. What's your favorite food?",
      "You seem really interesting. Can you tell me more about that?",
      "Goodbye! It was great meeting you today."
    ],
    negativeSamples: [
      "I feel really sad when my pet is sick.",
      "Let's solve this problem by sharing the playground equipment.",
      "I'm sorry that makes you feel nervous about the presentation.",
      "Can you please help me find the movie theater?",
      "The fast food worker asked what I wanted to order."
    ]
  },

  // 5. SOLUTION - Problem-solving and finding solutions to emotional situations
  solution: {
    keywords: {
      "solution": 2.5,
      "solve": 2.5,
      "problem": 2.0,
      "fix": 2.0,
      "help": 2.0,
      "better": 1.8,
      "change": 1.8,
      "improve": 1.8,
      "will": 1.6,
      "can": 1.5,
      "try": 1.6,
      "plan": 1.6,
      "idea": 1.6,
      "way": 1.4,
      "method": 1.5,
      "approach": 1.5,
      "strategy": 1.6,
      "work together": 1.8,
      "figure out": 1.7,
      "think about": 1.5,
      "come up with": 1.7,
      "great solution": 2.0,
      "good idea": 1.8,
      "that works": 1.6,
      "makes sense": 1.5,
      "fair": 1.6,
      "reasonable": 1.5
    },
    positiveSamples: [
      "I feel sad when friends ignore me. I will try talking to them about it.",
      "I feel mad when I lose games. I will practice more and be a good sport.",
      "I feel nervous about tests. I will study harder and ask for help.",
      "That's a great solution to help you feel better about the situation.",
      "We can solve this problem by working together and sharing ideas.",
      "I will try to be more patient when things don't go my way.",
      "Let's figure out a fair solution that helps everyone feel better."
    ],
    negativeSamples: [
      "My favorite game is soccer because I love running around.",
      "Hello! Nice to meet you. What's your name?",
      "I'm glad that makes you feel happy about your new bike.",
      "Can you please help me buy a snow cone?",
      "The clothing store worker helped me find a nice shirt."
    ]
  },

  // 6. CONFLICT - Resolving conflicts and disagreements
  conflict: {
    keywords: {
      "conflict": 2.5,
      "fight": 2.0,
      "argue": 2.0,
      "disagreement": 2.0,
      "problem": 2.0,
      "calm": 2.5,
      "deep breath": 2.5,
      "talk about": 2.0,
      "discuss": 1.8,
      "share": 1.8,
      "take turns": 2.0,
      "compromise": 2.0,
      "apologize": 2.0,
      "sorry": 2.0,
      "forgive": 1.8,
      "make it better": 2.2,
      "work it out": 2.0,
      "resolve": 2.0,
      "peaceful": 1.8,
      "fair": 1.8,
      "respect": 1.8,
      "understand": 1.8,
      "listen": 1.8,
      "seat": 1.4,
      "toy": 1.4,
      "shovel": 1.4,
      "view": 1.3,
      "bump": 1.3,
      "took": 1.4,
      "mine": 1.4,
      "yours": 1.4,
      "sharing": 1.8,
      "next time": 1.6
    },
    positiveSamples: [
      "I feel mad because you took my shovel. Can you tell me why?",
      "I'm sorry I took your seat. I didn't know you were sitting there.",
      "Let's take a deep breath and talk about this problem calmly.",
      "We can share the toy so both of us can play with it.",
      "I'm sorry I bumped into you. I'll be more careful next time.",
      "That's not fair, but let's work together to make it better.",
      "I understand you're upset. Let's find a solution that works for both of us."
    ],
    negativeSamples: [
      "My favorite animal is a cat because they're soft and cuddly.",
      "I feel happy when I play games with my friends.",
      "Hello! What's your name? Nice to meet you!",
      "I'm glad that makes you feel confident about your test.",
      "Can you help me find the bakery worker please?"
    ]
  },

  // 7. ADVOCACY - Self-advocacy and asking for help
  advocacy: {
    keywords: {
      "help": 2.5,
      "please": 2.5,
      "can you": 2.0,
      "excuse me": 2.0,
      "ask": 2.0,
      "question": 1.8,
      "need": 2.0,
      "want": 1.8,
      "would like": 1.8,
      "may I": 2.0,
      "could you": 2.0,
      "think": 1.6,
      "believe": 1.8,
      "achieve": 1.8,
      "confident": 1.8,
      "brave": 1.8,
      "speak up": 2.0,
      "stand up": 2.0,
      "voice": 1.6,
      "opinion": 1.6,
      "rights": 1.8,
      "fair": 1.6,
      "joyful jingle": 2.5,
      "feed birds": 1.8,
      "buy ticket": 1.8,
      "train": 1.4,
      "monkey": 1.4,
      "teacher": 1.6,
      "napkin": 1.4,
      "snack": 1.4,
      "find": 1.5,
      "where": 1.4,
      "how": 1.4,
      "what": 1.3,
      "thank you": 1.8,
      "thanks": 1.6,
      "manners": 1.8,
      "polite": 1.8,
      "respectful": 1.8
    },
    positiveSamples: [
      "Hi! Can I feed the birds, please? I'll be very careful.",
      "Excuse me, can you please tell me where to buy a train ticket?",
      "Hi! What do monkeys like to eat? I'm really curious to know.",
      "Can you help me find my teacher? I think I'm lost.",
      "Hi! Can I get a napkin, please? I spilled something.",
      "I believe in myself and I can achieve great things.",
      "Thank you for helping me. You're very kind and helpful."
    ],
    negativeSamples: [
      "My favorite color is green because it reminds me of nature.",
      "I feel sad when my friends don't include me in games.",
      "I'm sorry that makes you feel nervous about the presentation.",
      "Let's solve this problem by taking turns with the equipment.",
      "I'm sorry I took your toy without asking first."
    ]
  },

  // 8. COMMUNITY - Interacting with community workers and helpers
  community: {
    keywords: {
      "job": 2.0,
      "work": 2.0,
      "worker": 2.0,
      "helper": 1.8,
      "community": 2.0,
      "service": 1.8,
      "help": 1.8,
      "assist": 1.6,
      "serve": 1.6,
      // Specific workers and places
      "snow cone": 2.0,
      "lifeguard": 2.0,
      "beach": 1.6,
      "safe": 1.5,
      "ferris wheel": 2.0,
      "ticket": 1.8,
      "arcade": 2.0,
      "game": 1.5,
      "fast food": 2.0,
      "restaurant": 1.6,
      "order": 1.6,
      "hair stylist": 2.0,
      "salon": 1.8,
      "cut hair": 1.8,
      "clothing store": 2.0,
      "clothes": 1.6,
      "shirt": 1.4,
      "bakery": 2.0,
      "cupcake": 1.6,
      "bake": 1.6,
      "grocery store": 2.0,
      "groceries": 1.8,
      "buy": 1.5,
      "movie theater": 2.0,
      "movie": 1.6,
      "film": 1.4,
      // Common interactions
      "hello": 1.8,
      "hi": 1.8,
      "how are you": 1.8,
      "fine": 1.4,
      "good": 1.4,
      "great": 1.4,
      "wonderful": 1.5,
      "thank you": 1.8,
      "you're welcome": 1.8,
      "have a nice day": 1.8,
      "goodbye": 1.6,
      "bye": 1.6,
      "flavor": 1.5,
      "choice": 1.4,
      "favorite": 1.5,
      "like": 1.4,
      "enjoy": 1.4,
      "compliment": 1.6,
      "nice": 1.4,
      "beautiful": 1.4,
      "amazing": 1.4
    },
    positiveSamples: [
      "Hi! My name is Alex and I make snow cones. What's your favorite flavor?",
      "Hello! I'm a lifeguard and I help keep people safe at the beach.",
      "Hi! I work at the arcade. What's your favorite game to play?",
      "Hello! I'm a fast food worker. What would you like to order today?",
      "Hi! I'm a hair stylist. I love to cut hair and make people look great.",
      "Hello! I work at the clothing store. Can I help you find something?",
      "Hi! I work at the bakery and I love to bake cupcakes and cookies.",
      "Hello! I work at the grocery store. How are you today?",
      "Hi! I work at the movie theater. Can I see your ticket please?"
    ],
    negativeSamples: [
      "My favorite animal is a dog because they're loyal friends.",
      "I feel happy when I spend time with my family.",
      "I'm sorry that makes you feel sad about moving schools.",
      "Let's solve this problem by sharing the playground space.",
      "Can you help me find my teacher? I think I'm lost."
    ]
  }
};

const TopicAnalyzerDemo = () => {
  const [analysisOptions, setAnalysisOptions] = useState({
    keywordWeight: 0.6,  // Balanced - kids use simple keywords but avoid overfitting
    simWeight: 0.4,      // Moderate - helps with context understanding
    threshold: 0.25,     // Very forgiving - better for kids' varied speech
    topK: 3              // Focus on top matches only
  });

  return (
    <div style={{ padding: '20px' }}>
      <h1>Conversation Topic Classifier - Demo</h1>
      
      {/* Available Topics */}
      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <h3>Available Conversation Types:</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
          {Object.keys(topicConfigurations).map(topic => (
            <span
              key={topic}
              style={{
                padding: '8px 16px',
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                border: '1px solid #2196F3',
                borderRadius: '5px',
                textTransform: 'capitalize',
                fontSize: '14px'
              }}
            >
              {topic}
            </span>
          ))}
        </div>
        
        <div style={{ fontSize: '14px', color: '#666' }}>
          <p><strong>System will automatically detect which conversation type you're speaking about!</strong></p>
          <p><strong>Total Topics:</strong> {Object.keys(topicConfigurations).length} conversation types configured</p>
          <p><strong>Total Keywords:</strong> {Object.values(topicConfigurations).reduce((sum, config) => sum + Object.keys(config.keywords).length, 0)} across all topics</p>
        </div>
      </div>

      {/* Analysis Options */}
      {/* <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <h3>Analysis Options:</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label>Keyword Weight: {analysisOptions.keywordWeight}</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={analysisOptions.keywordWeight}
              onChange={(e) => setAnalysisOptions(prev => ({ ...prev, keywordWeight: parseFloat(e.target.value) }))}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label>Similarity Weight: {analysisOptions.simWeight}</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={analysisOptions.simWeight}
              onChange={(e) => setAnalysisOptions(prev => ({ ...prev, simWeight: parseFloat(e.target.value) }))}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label>Threshold: {analysisOptions.threshold}</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={analysisOptions.threshold}
              onChange={(e) => setAnalysisOptions(prev => ({ ...prev, threshold: parseFloat(e.target.value) }))}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label>Top K: {analysisOptions.topK}</label>
            <input
              type="range"
              min="1"
              max="10"
              step="1"
              value={analysisOptions.topK}
              onChange={(e) => setAnalysisOptions(prev => ({ ...prev, topK: parseInt(e.target.value) }))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div> */}

      {/* The Conversation Classifier Component */}
      <ConversationAnalyzer
        topicConfigurations={topicConfigurations}
        analysisOptions={analysisOptions}
      />
    </div>
  );
};

export default TopicAnalyzerDemo;
